import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { defaultRehypePlugins } from 'streamdown';

import { rehypeFootnoteCitations } from './plugins/rehype-footnote-citations';
import { rehypeParagraphToDiv } from './plugins/rehype-paragraph-to-div';

import type { Options as SanitizeSchema } from 'rehype-sanitize';
import type { PluggableList } from 'unified';

/**
 * Data attribute marking elements to exclude from copy operations (e.g. download buttons).
 * Applied as `data-copy-exclude` in HTML; accessed as `dataCopyExclude` in hast.
 */
export const COPY_EXCLUDE_ATTR = 'data-copy-exclude' as const;
export const COPY_EXCLUDE_SELECTOR = `[${COPY_EXCLUDE_ATTR}]` as const;

/**
 * Data attribute carrying the footnote citation number.
 * Applied as `data-footnote-number` in HTML; set as `dataFootnoteNumber` in hast properties.
 */
export const FOOTNOTE_NUMBER_ATTR = 'data-footnote-number' as const;
export const FOOTNOTE_NUMBER_HAST_PROP = 'dataFootnoteNumber' as const;

/**
 * Data attribute flagging an <a> as an inline citation.
 * Applied as `data-inline-citation` in HTML; set as `dataInlineCitation` in hast properties.
 */
export const INLINE_CITATION_ATTR = 'data-inline-citation' as const;
export const INLINE_CITATION_HAST_PROP = 'dataInlineCitation' as const;

/**
 * Value of the inline citation attribute for a `[source](https://...)` link that
 * points at a public website rather than the indexer. Such a citation has no
 * backend metadata, so CustomLink renders it from the href alone.
 */
export const INLINE_CITATION_WEB = 'web' as const;

/**
 * Regex matching [^N] footnote citation patterns in markdown text.
 * Note: Has the global flag. Create a new RegExp from `.source` to avoid shared lastIndex state.
 */
export const FOOTNOTE_PATTERN = /\[\^(\d+)\]/g;

/** Default remark plugins applied to all MarkdownRenderer instances. */
export const DEFAULT_REMARK_PLUGINS: PluggableList = [remarkGfm];

/**
 * Sanitization schema extending GitHub's defaults with custom data attributes
 * used by our rehype plugins. Callers needing additional attributes (e.g. diff
 * class names) can spread this and extend further.
 */
export const sanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    sup: [
      ...(defaultSchema.attributes?.['sup'] ?? []),
      FOOTNOTE_NUMBER_HAST_PROP,
    ],
    a: [...(defaultSchema.attributes?.['a'] ?? []), INLINE_CITATION_HAST_PROP],
  },
};

/**
 * Core rehype plugins that transform markdown structure (paragraphs, citations).
 * Does not include security plugins — use DEFAULT_STATIC_REHYPE_PLUGINS for a
 * complete chain, or spread these and append security plugins after custom plugins.
 */
export const CORE_REHYPE_PLUGINS: PluggableList = [
  rehypeParagraphToDiv,
  rehypeFootnoteCitations,
];

/** rehype-sanitize + rehype-harden with the shared schema. Append after all
 * custom plugins that set data attributes, since sanitize strips unknowns. */
export const SECURITY_REHYPE_PLUGINS: PluggableList = [
  [rehypeSanitize, sanitizeSchema],
  defaultRehypePlugins.harden,
];

/**
 * Default rehype plugins that don't require runtime data.
 * Plugins needing sources (like rehypeCitationLinks) are added at render time.
 * Variant renderers can import and spread these to extend with custom plugins.
 */
export const DEFAULT_STATIC_REHYPE_PLUGINS: PluggableList = [
  ...CORE_REHYPE_PLUGINS,
  ...SECURITY_REHYPE_PLUGINS,
];
